/** Common error-shape helpers for API-mutation surfaces.
 *
 * Backend conventions:
 *   - 400/403/404 errors carry a `detail` string in the response body
 *     for hand-written view-level validation.
 *   - Plain DRF serializer validation errors (the vast majority of
 *     ValidationErrors — e.g. a serializer field rejecting a value)
 *     have NO `detail` key at all: the response body is a dict keyed by
 *     FIELD NAME, each holding an array of error strings, e.g.
 *     `{"phone": ["The phone number entered is not valid."]}`. Every
 *     caller that only checked `detail` fell through to axios's generic
 *     "Request failed with status code 400" for this shape — which is
 *     any serializer field rejecting a value, not an edge case — so an
 *     operator got zero information about what to fix. 2026-09-17.
 *   - The MFA gate returns 403 with `reason: "mfa_required"` so the
 *     UI can show a clearer message + link the user to MFA setup
 *     instead of just saying "Could not save".
 */
import axios from "axios";


type ApiErrorPayload = {
  detail?: string | string[];
  reason?: string;
  // Any other key is a DRF field-error entry: string[] (or nested, for
  // a nested serializer) keyed by field name.
  [field: string]: unknown;
};


export function isMfaRequired(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  const data = err.response?.data as ApiErrorPayload | undefined;
  return err.response?.status === 403 && data?.reason === "mfa_required";
}


/** Flattens a DRF field-errors payload into "field: message" lines.
 *  Handles the common shapes: `string[]` per field, a bare `string`,
 *  and one level of nesting (a nested serializer's own field-errors
 *  dict) — deep enough for every validation error this app's
 *  serializers actually raise, without trying to handle arbitrary
 *  recursion. */
function flattenFieldErrors(data: Record<string, unknown>): string[] {
  const lines: string[] = [];
  for (const [field, value] of Object.entries(data)) {
    if (field === "detail" || field === "reason") continue;
    if (Array.isArray(value)) {
      const msgs = value.filter((v): v is string => typeof v === "string");
      if (msgs.length > 0) lines.push(`${field}: ${msgs.join(" ")}`);
    } else if (typeof value === "string") {
      lines.push(`${field}: ${value}`);
    } else if (value && typeof value === "object") {
      const nested = flattenFieldErrors(value as Record<string, unknown>);
      lines.push(...nested.map((l) => `${field}.${l}`));
    }
  }
  return lines;
}

export function describeApiError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiErrorPayload | undefined;
    if (data?.detail) {
      if (Array.isArray(data.detail)) return data.detail.join(" ");
      return data.detail;
    }
    if (data && typeof data === "object") {
      const lines = flattenFieldErrors(data);
      if (lines.length > 0) return lines.join(" ");
    }
    // A response came back but its body had nothing usable — the caller's
    // fallback ("Could not save changes") is more helpful than axios's
    // generic "Request failed with status code 500". Reserve `err.message`
    // for when there's genuinely no response (network/timeout/CORS), where
    // it actually says something ("Network Error") the fallback can't.
    if (!err.response) return err.message || fallback;
    return fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
